/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import Cookies from 'js-cookie';
import * as React from 'react';
import { useLocation } from 'react-router';
import { onCLS, onINP, onLCP, onFCP, onTTFB, type Metric } from 'web-vitals';

import {
    LoginActions,
    // recaptchav3Lib,
    SecurityHelper,
    useFlags,
    useLDClient,
} from 'sarsaparilla';
import { getRecommendationData } from '../utils/gaCommon';
import {
    clearBuyNowCheckoutMarker,
    getBuyNowCheckoutMarker,
} from '../utils/buyNowTracking';
import { useTypedDispatch } from '../redux';

const {
    getExternalCSRAccount,
    getGuestAccount,
    getRecAccount,
    isExternalCSRLoggedIn,
    isGuest,
    isLoggedIn,
} = SecurityHelper;

// ---------------------------------------------------------------------------------------
export function useUpdateReduxWithAccountServiceStatus() {
    const dispatch = useTypedDispatch();

    React.useEffect(() => {
        dispatch(LoginActions.updateAccountServiceStatus());
    }, [dispatch]);
}

// ---------------------------------------------------------------------------------------
export function useUpdateReduxWithAccountData() {
    const dispatch = useTypedDispatch();

    React.useEffect(() => {
        if (isLoggedIn()) {
            dispatch(LoginActions.loggedIn(getRecAccount()));
        } else if (isGuest()) {
            dispatch(LoginActions.loggedIn(getGuestAccount()));
        }

        // Note: When the CSR is impersonating a user, both isLoggedIn
        // and isExternalCSRLoggedIn will be true.
        if (isExternalCSRLoggedIn()) {
            dispatch(LoginActions.csrLoggedIn(getExternalCSRAccount()));
        }
    }, [dispatch]);
}

// ---------------------------------------------------------------------------------------
// export function useLoadRecaptcha() {
//     React.useEffect(() => {
//         recaptchav3Lib.loadScript(process.env.RECAPTCHAV3_SITE_KEY);
//     }, []);
// }

// ---------------------------------------------------------------------------------------
export function useGTMPageLoggingOnRouteChange() {
    const location = useLocation();

    React.useEffect(() => {
        // Bail early if dataLayer is not available
        if (!Array.isArray(window.dataLayer)) return;

        const recAccount = getRecAccount();
        const accountId = recAccount?.account?.account_id;

        //Check if the user is a CSR
        const csrStatus = isExternalCSRLoggedIn();

        // Get viewport dimensions
        const viewportWidth = window.innerWidth || 0;
        const viewportHeight = window.innerHeight || 0;

        if (accountId) {
            window.dataLayer.push({
                userId: accountId,
                ...(csrStatus && { isCSRLoggedIn: 'CSR User' }),
                event: 'user_data_ready',
                viewportWidth,
                viewportHeight,
            });

            window.medallia_data = {
                userId: accountId,
            };
        } else {
            // Fire event even without user data
            window.dataLayer.push({
                event: 'user_data_ready',
                viewportWidth,
                viewportHeight,
            });
        }
    }, [location]);
}

// ---------------------------------------------------------------------------------------
export function useHotjarStateChangeOnRouteChange() {
    const location = useLocation();

    React.useEffect(() => {
        const hotjar = (window as any).hj;
        if (typeof hotjar !== 'function') return;

        hotjar('stateChange', location.pathname);
    }, [location.pathname]);
}

// ---------------------------------------------------------------------------------------
export function useHotjarIdentifyOnRouteChange() {
    const location = useLocation();
    const { campingFacilityPageV3 } = useFlags();
    const lastIdentifyPayloadRef = React.useRef<string>('');

    React.useEffect(() => {
        const hotjar = (window as any).hj;
        if (typeof hotjar !== 'function') return;

        const recAccountId = getRecAccount()?.account?.account_id;
        const guestAccountId = getGuestAccount()?.account?.account_id;
        const userId = recAccountId || guestAccountId;
        if (!userId) return;

        const attributes: Record<string, string> = {};
        if (typeof campingFacilityPageV3 === 'boolean') {
            attributes.campingFacilityPageV3 = String(campingFacilityPageV3);
        }

        const payloadFingerprint = JSON.stringify({ userId, attributes });
        if (lastIdentifyPayloadRef.current === payloadFingerprint) return;

        hotjar('identify', userId, attributes);
        lastIdentifyPayloadRef.current = payloadFingerprint;
    }, [location.pathname, campingFacilityPageV3]);
}

// ---------------------------------------------------------------------------------------
const LD_IDENTIFY_TIMEOUT_MS = 3000;

export function useFeatureFlagSettingForGTM() {
    const isSavedRef = React.useRef(false);
    const [timedOut, setTimedOut] = React.useState(false);
    const flags = useFlags();
    const ldClient = useLDClient();

    // Safety net: if ldClient.identify() never completes (LD unreachable, user
    // never logs in, etc.), force the push after 3s with whatever values are
    // available — better to push anonymous-context defaults than nothing.
    React.useEffect(() => {
        const t = window.setTimeout(() => setTimedOut(true), LD_IDENTIFY_TIMEOUT_MS);
        return () => window.clearTimeout(t);
    }, []);

    React.useEffect(() => {
        if (isSavedRef.current) return;

        // Wait for ldClient.identify() to complete for logged-in users so we
        // get user-targeted flag values rather than anonymous-bucket defaults.
        // The guard is bypassed once the timeout fires.
        if (!timedOut) {
            const ldContext = ldClient?.getContext();
            if (isLoggedIn() && ldContext?.anonymous) return;
        }

        const analyticsTrackedFlags = flags['analyticsTrackedFlags'] as
            string[] | undefined;

        try {
            if (
                window.dataLayer &&
                analyticsTrackedFlags &&
                analyticsTrackedFlags?.length
            ) {
                const dataLayer: Record<string, string> = {
                    event: 'launch-darkly-set',
                    identifier: 'launch-darkly-flags',
                };

                analyticsTrackedFlags.forEach((k) => {
                    dataLayer[`launch-darkly-flag-${k}`] = flags?.[k];
                });

                window.dataLayer.push(dataLayer);
                isSavedRef.current = true;
            }
        } catch {
            // eslint-disable-next-line no-console
            console.error('Caught error that needs to be piped to error reporting');
        }
    }, [flags, ldClient, timedOut]);
}

// ---------------------------------------------------------------------------------------
export function useWebVitalsInGTM() {
    // initialize web vitals on load
    React.useEffect(() => {
        const pushToDataLayer = ({ name, value, id, rating }: Metric) => {
            const params = {
                event: 'web_vitals',
                metric_name: name, // 'LCP', 'INP', 'CLS', etc.
                metric_value: name === 'CLS' ? +value.toFixed(4) : Math.round(value),
                metric_id: id,
                metric_rating: rating, // 'good', 'needs-improvement', or 'poor'
                device_memory_gb: (navigator as any).deviceMemory,
                effective_type: (navigator as any).connection?.effectiveType,
            };

            // Sampling (10%)
            if (Math.random() < 0.1) {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push(params);
            }
        };

        onLCP(pushToDataLayer);
        onINP(pushToDataLayer);
        onCLS(pushToDataLayer);
        onFCP(pushToDataLayer);
        onTTFB(pushToDataLayer);
    }, []);
}

// ---------------------------------------------------------------------------------------
/**
 * Intercepts dataLayer pushes for ecommerce events and enriches them with recommendation data
 * Handles: view_item, add_to_cart, begin_checkout, view_cart, add_payment_info, purchase
 */
export function useEnrichAddToCartEvents() {
    React.useEffect(() => {
        if (!window.dataLayer || !Array.isArray(window.dataLayer)) return;

        // Store original push method
        const originalPush = window.dataLayer.push.bind(window.dataLayer);

        // Override push method to intercept add_to_cart events
        window.dataLayer.push = function (data: any) {
            // If this is an ecommerce event, enrich it with recommendation data
            // (checkout events often fire in sequence and can overwrite the ecommerce data)
            const ecommerceEvents = [
                'view_item',
                'add_to_cart',
                'begin_checkout',
                'view_cart',
                'add_payment_info',
                'purchase',
            ];
            if (ecommerceEvents.includes(data?.event) && data?.ecommerce?.items) {
                const enrichedItems = data.ecommerce.items.map((item: any) => {
                    // If item already has recommendation data, keep it
                    if (item.was_recommended) {
                        return item;
                    }

                    // If no item_id or facility_id, can't lookup - return as-is
                    if (!item.item_id && !item.facility_id) {
                        return item;
                    }

                    // Try to get recommendation data using centralized function (includes expiration check)
                    let recommendationData = null;
                    try {
                        if (item.item_id || item.facility_id) {
                            recommendationData = getRecommendationData(
                                item.item_id || item.facility_id || '',
                                item.item_id
                            );
                        }
                    } catch (e) {
                        // Silently handle errors
                    }

                    // Enrich item with recommendation data if found
                    if (recommendationData) {
                        return {
                            ...item,
                            was_recommended: recommendationData.was_recommended,
                            recommended_location: recommendationData.recommended_location,
                            ...(recommendationData.previous_booking_dates && {
                                previous_booking_dates:
                                    recommendationData.previous_booking_dates,
                            }),
                        };
                    }

                    return item;
                });

                // Update the event data with enriched items
                data.ecommerce.items = enrichedItems;

                // Also add event-level parameters for GTM access (preserve if already set by sarsaparilla)
                const hasRecommendation = enrichedItems.some(
                    (item: any) => item.was_recommended
                );
                if (hasRecommendation) {
                    const recommendedItem = enrichedItems.find(
                        (item: any) => item.was_recommended
                    );
                    if (recommendedItem) {
                        // Only set if not already set by sarsaparilla
                        if (!data.event_was_recommended) {
                            data.event_was_recommended = recommendedItem.was_recommended;
                        }
                        if (!data.event_recommended_location) {
                            data.event_recommended_location =
                                recommendedItem.recommended_location;
                        }
                        if (
                            !data.event_previous_booking_dates &&
                            recommendedItem.previous_booking_dates
                        ) {
                            data.event_previous_booking_dates =
                                recommendedItem.previous_booking_dates;
                        }
                    }
                }
            }

            const buyNowAttributableEvents = [
                'begin_checkout',
                'view_cart',
                'add_payment_info',
                'purchase',
            ];
            if (buyNowAttributableEvents.includes(data?.event)) {
                const buyNowMarker = getBuyNowCheckoutMarker();
                if (buyNowMarker) {
                    if (!data.event_is_buy_now) {
                        data.event_is_buy_now = true;
                    }
                    if (!data.event_buy_now_source && buyNowMarker.source) {
                        data.event_buy_now_source = buyNowMarker.source;
                    }
                    if (data?.event === 'purchase') {
                        clearBuyNowCheckoutMarker();
                    }
                }
            }

            // Call original push with (potentially enriched) data
            return originalPush(data);
        };

        // Cleanup: restore original push on unmount
        return () => {
            if (window.dataLayer && window.dataLayer.push !== originalPush) {
                window.dataLayer.push = originalPush;
            }
        };
    }, []);
}

// ---------------------------------------------------------------------------------------
// 1024 (DynamoDB sort key limit) - 17 (AI_REFERRAL_LINK: prefix)
const MAX_CONVERSATION_ID_LENGTH = 1007;
// Slash allowed: OpenAI's session id (piped as conversation_id) is versioned,
// e.g. "v1/4sg5...". URLSearchParams decodes it before this test runs.
const CONVERSATION_ID_PATTERN = new RegExp(
    `^[a-zA-Z0-9_.:/-]{1,${MAX_CONVERSATION_ID_LENGTH}}$`
);

export function useAIReferralCapture() {
    React.useEffect(() => {
        let currentUrl: URL;
        try {
            currentUrl = new URL(window.location.href);
        } catch {
            return;
        }
        const params = new URLSearchParams(currentUrl.search);
        const conversationId = params.get('conversation_id');

        if (!conversationId || !CONVERSATION_ID_PATTERN.test(conversationId)) {
            if (conversationId) {
                params.delete('conversation_id');
                const newURL = `${currentUrl.pathname}${params.toString() ? `?${params.toString()}` : ''}${currentUrl.hash}`;
                window.history.replaceState({}, '', newURL);
            }
            return;
        }

        Cookies.set('conversation_id', conversationId, {
            expires: 2,
            sameSite: 'Strict',
            secure: true,
            path: '/',
        });

        try {
            window.sessionStorage.setItem('conversation_id', conversationId);
        } catch {
            // sessionStorage blocked
        }

        params.delete('conversation_id');
        const newURL = `${currentUrl.pathname}${params.toString() ? `?${params.toString()}` : ''}${currentUrl.hash}`;
        window.history.replaceState({}, '', newURL);
    }, []);
}

// cSpell:ignore medallia, TTFB, hotjar, ecommerce
